import { cn } from '../../lib/cn';
import { signedPoints } from '../../lib/format';
import { shortRider, type DuelVM } from '../../lib/cyclingRound';
import { parseRiderName, riderSubtitle } from '../../lib/riderDisplay';
import { ConfidenceBadge } from './shared';
import { ChevronRight, Check, X } from '../ui/icons';

/** One rider line on the live board — surname-first full name + team·flag subtitle,
 *  highlighted when it's the backed rider (matching the Call the Duels pick screen). */
function DuelRiderLine({
  name,
  team,
  nationality,
  backed
}: {
  name: string;
  team?: string | null;
  nationality?: string | null;
  backed: boolean;
}) {
  const { surname, firstName } = parseRiderName(name);
  const subtitle = riderSubtitle(team, nationality);
  return (
    <div className={cn('min-w-0', !backed && 'opacity-55')}>
      <div className={cn('truncate text-[14px] tracking-snug', backed ? 'text-accent' : 'text-white')}>
        <span className="font-extrabold">{surname}</span>
        {firstName ? (
          <span className={cn('font-medium', backed ? 'text-accent/75' : 'text-white/55')}> {firstName}</span>
        ) : null}
      </div>
      {subtitle ? (
        <div className={cn('mt-[1px] truncate text-[10.5px] font-semibold', backed ? 'text-accent/70' : 'text-white/45')}>
          {subtitle}
        </div>
      ) : null}
    </div>
  );
}

/** The duel matchup with the player's backed rider picked out in accent. */
function DuelMatchup({ vm, dark = false }: { vm: DuelVM; dark?: boolean }) {
  const aBacked = vm.pick === 'A';
  const backed = dark ? 'text-accent' : 'text-accent-ink';
  const faded = dark ? 'text-white/45' : 'text-ink3';
  return (
    <span className={cn('text-[14px] font-extrabold tracking-snug', dark ? 'text-white' : 'text-ink')}>
      <span className={aBacked ? backed : faded}>{shortRider(vm.riderAName)}</span>
      <span className={cn('px-1 font-semibold', dark ? 'text-white/35' : 'text-ink4')}>v</span>
      <span className={!aBacked ? backed : faded}>{shortRider(vm.riderBName)}</span>
    </span>
  );
}

/** A small "your pick" / outcome chip by duel state. */
function DuelStatusChip({ vm, dark = false }: { vm: DuelVM; dark?: boolean }) {
  const backed = dark ? 'text-accent' : 'text-accent-ink';
  if (vm.status === 'void') {
    return <span className={cn('text-[10px] font-bold tracking-wide', dark ? 'text-white/40' : 'text-ink3')}>VOID</span>;
  }
  if (vm.status === 'settled') {
    return vm.correct ? (
      <span className={cn('inline-flex items-center gap-1 text-[10px] font-extrabold', backed)}>
        <Check width={11} height={11} strokeWidth={3.2} /> Won
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 text-[10px] font-extrabold text-miss">
        <X width={11} height={11} strokeWidth={3.2} /> Lost
      </span>
    );
  }
  return (
    <span className={cn('text-[10px] font-semibold', dark ? 'text-white/55' : 'text-ink2')}>
      Backing {shortRider(vm.pickedName)}
    </span>
  );
}

/**
 * One duel on the live/waiting board. Shows the confidence weight, the matchup with
 * your backed rider highlighted, and the points it will bank (or the
 * "backing X" state pre-settle). The whole row opens the duel detail.
 */
export function DuelOverviewRow({ vm, onOpen }: { vm: DuelVM; onOpen?: () => void }) {
  const tappable = !!onOpen;
  const decided = vm.status !== 'upcoming';
  const aBacked = vm.pick === 'A';
  return (
    <div
      role={tappable ? 'button' : undefined}
      tabIndex={tappable ? 0 : undefined}
      onClick={onOpen}
      onKeyDown={
        tappable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen?.();
              }
            }
          : undefined
      }
      className={cn(
        'rounded-[20px] border border-white/[0.08] bg-white/[0.05] p-[14px_15px]',
        tappable && 'tap cursor-pointer text-left active:bg-white/[0.07]'
      )}
    >
      <div className="flex items-center gap-3">
        <div className="flex w-[34px] flex-col items-center">
          <ConfidenceBadge weight={vm.confidence} active={vm.status === 'settled' && !!vm.correct} dark />
        </div>

        <div className="min-w-0 flex-1">
          <DuelRiderLine name={vm.riderAName} team={vm.riderATeam} nationality={vm.riderANat} backed={aBacked} />
          <div className="flex items-center gap-2 py-[5px]">
            <div className="h-px flex-1 bg-white/[0.08]" />
            <span className="text-[8.5px] font-extrabold tracking-[0.14em] text-white/35">VS</span>
            <div className="h-px flex-1 bg-white/[0.08]" />
          </div>
          <DuelRiderLine name={vm.riderBName} team={vm.riderBTeam} nationality={vm.riderBNat} backed={!aBacked} />

          <div className="mt-2 flex items-center gap-2">
            <DuelStatusChip vm={vm} dark />
          </div>
        </div>

        {decided && vm.points != null ? (
          <div className="self-start text-right">
            <div className={cn('num text-[18px] font-extrabold leading-none', vm.points > 0 ? 'text-accent' : 'text-white')}>
              {signedPoints(vm.points)}
            </div>
            <div className="mt-[3px] text-[10px] font-semibold text-white/45">{vm.status === 'void' ? 'void' : 'final'}</div>
          </div>
        ) : null}

        {tappable && <ChevronRight width={16} height={16} strokeWidth={2.6} className="-mr-1 shrink-0 self-start text-white/30" />}
      </div>
    </div>
  );
}

/** One duel on the settled results recap: matchup, your call, and the points scored. */
export function DuelScoreRow({ vm }: { vm: DuelVM }) {
  const won = vm.status === 'settled' && !!vm.correct;
  return (
    <div className="rounded-[20px] border border-black/5 bg-card p-[14px_15px] shadow-tile">
      <div className="flex items-center gap-3">
        <div className="flex w-[34px] flex-col items-center">
          <ConfidenceBadge weight={vm.confidence} active={won} />
        </div>
        <div className="min-w-0 flex-1">
          <DuelMatchup vm={vm} />
          <div className="mt-1.5">
            <DuelStatusChip vm={vm} />
          </div>
        </div>
        <div className="text-right">
          <div className="num text-[11px] font-semibold text-ink2">
            {vm.status === 'void' ? 'void' : `10 × ${vm.confidence}`}
          </div>
          <div className={cn('num text-[18px] font-extrabold leading-tight', won ? 'text-accent-ink' : 'text-ink')}>
            {vm.points != null ? signedPoints(vm.points) : '—'}
          </div>
        </div>
      </div>
    </div>
  );
}
